'use client';

import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';

import { Select, SelectItem, Checkbox, Button, Pagination, Card, CardBody } from '@nextui-org/react';
import ArticleCard from './components/ArticleCard';
import { getDictionaryClient } from '../../../get-dictionary-client';

interface ArticlesPageProps {
  params: {
    lang: string;
  };
}

interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  published: boolean;
  featured: boolean;
  state: string;
  typeArticle: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  viewCount: number;
  readTime?: number;
  featuredImage?: string;
  featuredImageAlt?: string;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string;
  author: {
    id: string;
    name: string;
    email: string;
  };
  category?: {
    id: string;
    name: string;
    slug: string;
  };
  tags: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
}

interface ArticlesResponse {
  articles: Article[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

const selectClassNames: Partial<Record<'base' | 'trigger' | 'value' | 'label' | 'helper' | 'listbox' | 'listboxWrapper' | 'popoverContent' | 'selectorIcon', string>> = {
  trigger:
    'bg-gray-50/90 dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 transition-colors data-[hover=true]:border-blue-500 data-[focus-visible=true]:border-blue-500 data-[focus-visible=true]:ring-2 data-[focus-visible=true]:ring-blue-500/30',
  value:
    'text-gray-900 dark:text-gray-100 group-data-[placeholder=true]:text-gray-500 group-data-[placeholder=true]:dark:text-gray-400',
  listbox: 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  listboxWrapper: 'bg-white dark:bg-gray-800',
  popoverContent: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg',
  selectorIcon: 'text-gray-500 dark:text-gray-300',
};

export default function ArticlesPage({ params }: ArticlesPageProps) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [limit, setLimit] = useState(10);
  const [categories, setCategories] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [dictionary, setDictionary] = useState<any>(null);
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false);

  const router = useRouter();
  const searchParams = useSearchParams();
  const { lang } = params;

  useEffect(() => {
    // Load dictionary
    getDictionaryClient(lang as 'en' | 'ua').then(setDictionary);

    // Initialize filters from URL params
    const page = parseInt(searchParams.get('page') || '1');
    const search = searchParams.get('search') || '';
    const category = searchParams.get('category') || '';
    const type = searchParams.get('type') || '';
    const featured = searchParams.get('featured') === 'true';
    const limitParam = parseInt(searchParams.get('limit') || '10');

    setSearchQuery(search);
    setSelectedCategory(category);
    setSelectedType(type);
    setFeaturedOnly(featured);
    setLimit(limitParam);

    fetchArticles(page, search, category, type, featured, limitParam);
    fetchCategories();
  }, [searchParams]);

  // Update pagination limit when limit changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      limit: limit
    }));
  }, [limit]);

  const fetchArticles = async (
    page: number = 1,
    search: string = '',
    categoryId: string = '',
    type: string = '',
    featured: boolean = false,
    limit: number = 10
  ) => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        status: 'PUBLISHED',
        sortBy: 'publishedAt',
        sortOrder: 'desc',
      });

      if (search) queryParams.set('search', search);
      if (categoryId) queryParams.set('categoryId', categoryId);
      if (type) queryParams.set('typeArticle', type);
      if (featured) queryParams.set('featured', 'true');

      const response = await fetch(`/api/articles?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch articles');
      }

      const data: ArticlesResponse = await response.json();

      // Filter for published articles only
      const publishedArticles = data.articles.filter(
        article => article.published && article.state === 'PUBLISHED'
      );

      setArticles(publishedArticles);
      setPagination(data.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilters({ search: searchQuery, page: 1 });
  };

  const updateFilters = (filters: {
    search?: string;
    category?: string;
    type?: string;
    featured?: boolean;
    page?: number;
    limit?: number;
  }) => {
    const params = new URLSearchParams(searchParams);

    if (filters.search !== undefined) {
      if (filters.search) {
        params.set('search', filters.search);
      } else {
        params.delete('search');
      }
    }

    if (filters.category !== undefined) {
      if (filters.category) {
        params.set('category', filters.category);
      } else {
        params.delete('category');
      }
    }

    if (filters.type !== undefined) {
      if (filters.type) {
        params.set('type', filters.type);
      } else {
        params.delete('type');
      }
    }

    if (filters.featured !== undefined) {
      if (filters.featured) {
        params.set('featured', 'true');
      } else {
        params.delete('featured');
      }
    }

    if (filters.page !== undefined) {
      if (filters.page > 1) {
        params.set('page', filters.page.toString());
      } else {
        params.delete('page');
      }
    }

    if (filters.limit !== undefined) {
      if (filters.limit !== 10) {
        params.set('limit', filters.limit.toString());
      } else {
        params.delete('limit');
      }
    }

    router.push(`/${lang}/articles?${params.toString()}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSelectedType('');
    setFeaturedOnly(false);
    setLimit(10);
    router.push(`/${lang}/articles`);
  };

  // Results Info Component
  const ResultsInfo = () => (
    <div className="flex justify-end mb-6">
      <div className="flex items-center space-x-4 text-right">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {pagination.total > 0 ? (
            dictionary.articles.showingResults
              .replace('{{start}}', (((pagination.page - 1) * limit) + 1).toString())
              .replace('{{end}}', Math.min(pagination.page * limit, pagination.total).toString())
              .replace('{{total}}', pagination.total.toString())
          ) : (
            dictionary.articles.noArticlesFound
          )}
        </p>
        {pagination.total > 0 && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            {pagination.total} total
          </span>
        )}
      </div>
    </div>
  );

  if (!dictionary) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gray-50 dark:bg-gray-900 transition-colors">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-6">
        {/* Filters */}
        <Card shadow='sm' className="border border-gray-200 dark:border-gray-800 mt-2 lg:mt-0 mb-8">
          <CardBody className="gap-6 sm:gap-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              {/* Header */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  {dictionary.articles.pageTitle}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  {dictionary.articles.pageDescription}
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <Button size="sm" variant="flat" onPress={() => setIsFiltersCollapsed((prev) => !prev)}>
                  {isFiltersCollapsed ? 'Expand filters' : 'Collapse filters'}
                </Button>
              </div>
            </div>
            
            {/* Search */}
            <div
              data-collapse={isFiltersCollapsed ? 'true' : 'false'}
              className={isFiltersCollapsed ? 'hidden' : 'block'}
            >
              <form onSubmit={handleSearch} className="space-y-4">                
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <label htmlFor="search" className="sr-only">{dictionary.articles.searchLabel}</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <input
                        id="search"
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        placeholder={dictionary.articles.searchPlaceholder}
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    color="primary"
                    className="px-4 py-2"
                  >
                    {dictionary.articles.searchButton}
                  </Button>
                </div>

                {/* Filter Options */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  {/* Category Filter */}
                  <div className="flex flex-col justify-end">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {dictionary.articles.categoryLabel}
                    </label>
                    <Select
                      id="category"
                      selectedKeys={selectedCategory ? [selectedCategory] : []}
                      onSelectionChange={(keys) => {
                        const selectedValue = Array.from(keys)[0] as string;
                        setSelectedCategory(selectedValue || '');
                        updateFilters({ category: selectedValue || '', page: 1 });
                      }}
                      placeholder={dictionary.articles.categoryPlaceholder}
                      className="w-full"
                      variant="bordered"
                      radius="md"
                      aria-label={dictionary.articles.categoryLabel}
                      classNames={selectClassNames}
                    >
                      {[
                        { key: '', label: dictionary.articles.categoryAll },
                        ...categories.map((category) => ({
                          key: category.id,
                          label: category.name
                        }))
                      ].map((item) => (
                        <SelectItem key={item.key} value={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  {/* Type Filter */}
                  <div className="flex flex-col justify-end">
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {dictionary.articles.typeLabel}
                    </label>
                    <Select
                      id="type"
                      selectedKeys={selectedType ? [selectedType] : []}
                      onSelectionChange={(keys) => {
                        const selectedValue = Array.from(keys)[0] as string;
                        setSelectedType(selectedValue || '');
                        updateFilters({ type: selectedValue || '', page: 1 });
                      }}
                      placeholder={dictionary.articles.typePlaceholder}
                      className="w-full"
                      variant="bordered"
                      radius="md"
                      aria-label={dictionary.articles.typeLabel}
                      classNames={selectClassNames}
                    >
                      {[
                        { key: '', label: dictionary.articles.typeAll },
                        { key: 'NEWS', label: dictionary.articles.typeNews },
                        { key: 'PAGE', label: dictionary.articles.typePage },
                        { key: 'ARTICLE', label: dictionary.articles.typeArticle },
                        { key: 'REPORT', label: dictionary.articles.typeReport },
                        { key: 'PROGRAM', label: dictionary.articles.typeProgram }
                      ].map((item) => (
                        <SelectItem key={item.key} value={item.key}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>

                  {/* Limit Filter */}
                  <div className="flex flex-col justify-end">
                    <label htmlFor="limit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Show per page
                    </label>
                    <Select
                      id="limit"
                      selectedKeys={[limit.toString()]}
                      onSelectionChange={(keys) => {
                        const selectedValue = parseInt(Array.from(keys)[0] as string);
                        setLimit(selectedValue);
                        updateFilters({ limit: selectedValue, page: 1 });
                      }}
                      className="w-full"
                      variant="bordered"
                      radius="md"
                      aria-label="Items per page"
                      classNames={selectClassNames}
                    >
                      <SelectItem key="10" value="10">10</SelectItem>
                      <SelectItem key="25" value="25">25</SelectItem>
                      <SelectItem key="50" value="50">50</SelectItem>
                    </Select>
                  </div>

                  {/* Featured Filter */}
                  <div className="flex flex-col justify-end">
                    <label className="flex items-center space-x-2">
                      <Checkbox
                        isSelected={featuredOnly}
                        onValueChange={(isSelected) => {
                          setFeaturedOnly(isSelected);
                          updateFilters({ featured: isSelected, page: 1 });
                        }}
                        className="text-blue-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{dictionary.articles.featuredOnly}</span>
                    </label>
                  </div>

                  {/* Clear Filters */}
                  <div className="flex flex-col justify-end">
                    <Button
                      type="button"
                      variant="bordered"
                      onClick={clearFilters}
                      className="text-sm"
                    >
                      {dictionary.articles.clearFilters}
                    </Button>
                  </div>
                </div>
              </form>
            </div>

          </CardBody>
        </Card>

        



        {/* Results Info */}
        <ResultsInfo />

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Articles Grid */}
        {articles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} lang={lang} />
            ))}
          </div>
        ) : !loading && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{dictionary.articles.emptyStateTitle}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {dictionary.articles.emptyStateDescription}
            </p>
          </div>
        )}

        {/* Results Info Bottom */}
        {articles.length > 0 && <ResultsInfo />}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center px-4 py-4">

            <Pagination
              total={pagination.totalPages}
              page={pagination.page}
              onChange={(page) => updateFilters({ page })}
              showControls
              showShadow
              color="primary"
              size="lg"
            />
          </div>
        )}
      </div>
    </div>
  );
}
